import { useCallback, useRef, useEffect } from 'react';
import useProductTypeHelpers from '@hooks/useProductTypeHelpers';
import useConfig from '@hooks/useConfig';
import { round, isPlainObject, isString, uniq, groupBy } from 'lodash';
import { DYNAMIC_RATES_ENDPOINT } from '@constants';
import { keys, productLoadState, closingCostType } from '@enums';
import CalculatorError from '@utils/CalculatorError';
import {
  generateId,
  camelCaseProps,
  isFixedRate,
  isAdjustableRate,
  isPurchase,
  getEffectiveDate,
  filterRequestPhaseData
} from '@helpers';

export default function useScenarioHelpers(requestPhaseData, productTypes) {
  const config = useConfig();

  const {
    labels,
    productIsEnabled,
    createLoadingState,
    getProductsToLoad,
    getEnabledProducts,
    getRequestPhaseWithProductsToLoad,
    updateLoadingState
  } = useProductTypeHelpers(productTypes);

  const productTypesRef = useRef(productTypes);

  useEffect(() => {
    productTypesRef.current = productTypes;
  }, [productTypes]);

  // Store data from each request phase in a different
  // nested array so that we only show additional
  // when the user clicks load more
  const createRatesArray = useCallback(requestPhases => {
    return new Array(requestPhases?.length ?? 0).fill([]);
  }, []);

  const fetchRates = useCallback(
    async (
      loanDetails,
      requestPhaseID,
      productTypeIDs,
      closingCosts = closingCostType.BOTH,
      debugErrorCode
    ) => {
      const body = {
        requestPhaseID,
        productTypeIDs,
        closingCostType: closingCosts,
        ...loanDetails
      };

      if (isPurchase(loanDetails.loanPurpose)) {
        body.cashOutAmount = null;
      }

      if (config.enableDebugMode && debugErrorCode) {
        body.debugErrorCode = debugErrorCode;
      }

      const args = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip, deflate',
          Accept: '*/*'
        },
        body: JSON.stringify(body)
      };

      const response = await fetch(DYNAMIC_RATES_ENDPOINT, args);

      if (!response.ok) {
        throw new CalculatorError();
      }

      const data = camelCaseProps(await response.json());

      if (isPlainObject(data) && !data.errors && data.rates?.length) {
        data.effectiveDate = getEffectiveDate(data.effectiveDate);
      } else {
        data.effectiveDate = null;
      }

      return data;
    },
    [config.enableDebugMode]
  );

  const formatData = useCallback(
    data => {
      if (!Array.isArray(data)) {
        return [];
      }

      return data
        .map(p => {
          if (!p.productTypeID) {
            console.error('Invalid product type', p);
            return null;
          }

          p.type = p.type.toLowerCase();
          p.isAdjustable = isAdjustableRate(p.type);
          p.isFixed = isFixedRate(p.type);
          p.rate = round(p.rate, 3);
          p.apr = round(p.apr, 3);
          p.payment = round(p.payment, 2);
          p.closingCosts = round(p.closingCosts, 2);
          p.term = parseFloat(p.term);
          p.months = parseFloat(p.months);

          const productLabels = labels.current[String(p.productTypeID)];

          p.productLabel = productLabels?.name.trim();

          if (p.isAdjustable) {
            p.changeFrequencyUnits =
              productLabels?.changeFrequencyUnits ?? 'years';

            p.timePeriodOfInitialRate = parseInt(p.timePeriodOfInitialRate);
            p.rateChangeFrequency = parseInt(p.rateChangeFrequency);
          }

          // We store a numeric value for the term for sorting
          p.termValue = p.isAdjustable
            ? parseInt(
                `${p.timePeriodOfInitialRate / 12}${p.rateChangeFrequency}`
              )
            : parseInt(p.term);

          // Product badges
          if (!p.badges || !Array.isArray(p.badges)) {
            p.badges = isString(p.badges) ? p.badges.split(';') : [];
          }

          p.badges = p.badges
            .map(badgeName => (isString(badgeName) ? badgeName.trim() : null))
            .filter(badgeName => !!badgeName);

          // Generate hash used to deduplicate rates if necessary
          p.hash = generateId({
            planNumber: p.planNumber,
            rate: p.rate,
            apr: p.apr,
            payment: p.payment,
            closingCosts: p.closingCosts
          });

          // Fallback in case product label not found
          if (!p.productLabel) {
            if (p.isFixed) {
              p.productLabel = `${p.term} year fixed rate`;
            } else if (p.isAdjustable) {
              const r = new RegExp(
                `[^/]\\d/${p.rateChangeFrequency}[a-z ]`,
                'i'
              );
              const match = p.planDescription?.match(r);
              p.productLabel = match
                ? `${match[0].trim()} adjustable rate`
                : null;
            }
          }
          // Validate data
          if (
            !p.term ||
            !p.productLabel ||
            p.apr === 0 ||
            p.rate === 0 ||
            isNaN(p.apr) ||
            isNaN(p.rate) ||
            isNaN(p.payment) ||
            isNaN(p.closingCosts) ||
            (!p.isAdjustable && !p.isFixed)
          ) {
            if (config.enableDebugMode) {
              console.warn('Invalid product', p);
            }

            return null;
          }

          return p;
        })
        .filter(p => p);
    },
    [labels, config.enableDebugMode]
  );

  // Creates a base state object for a given set of loan details
  const createScenario = useCallback(
    (loanDetails, initialRateData) => {
      [keys.LOAN_AMOUNT, keys.HOME_VALUE, keys.ZIP_CODE].forEach(key => {
        if (loanDetails[key]) {
          loanDetails[key] = String(loanDetails[key]);
        }
      });

      const requestPhases = filterRequestPhaseData(
        loanDetails,
        requestPhaseData
      );

      const scenario = {
        loanDetails,
        requestPhases,
        id: generateId(loanDetails),
        data: createRatesArray(requestPhases),
        loadingState: createLoadingState(requestPhases),
        effectiveDates: [{ requestPhaseIndex: 0, date: new Date() }],
        rates: [],
        errors: null,
        visibleRequestPhaseIndex: 0,
        hasInitialData: false,
        hasCachedRateData: false
      };

      // Mark loading complete for request phases
      // that don't include a particular product
      scenario.loadingState.forEach(product => {
        product.requestPhases = product.requestPhases.map((state, index) => {
          const requestPhaseProducts = requestPhases[index].products;
          const hasProduct = !!requestPhaseProducts.find(
            r => r.productTypeID === product.productTypeID
          );

          return hasProduct ? state : productLoadState.COMPLETE;
        });
      });

      // Cached rates for the initial scenario are passed
      // as props so that they can display on initial render
      if (initialRateData?.rates?.length) {
        const data = camelCaseProps(initialRateData);
        const rates = formatData(data.rates);
        const productTypeIDs = uniq(rates.map(r => r.productTypeID));

        scenario.data[0] = rates;
        scenario.hasCachedRateData = true;
        scenario.hasInitialData = true;
        scenario.effectiveDates[0].date = getEffectiveDate(data.effectiveDate);

        if (!requestPhases[0].separateNCC) {
          scenario.loadingState = updateLoadingState(
            productLoadState.COMPLETE,
            scenario.loadingState,
            productTypeIDs
          );
        } else {
          const shouldLoad = (type, productTypeID) => {
            const nccValue = type === 'ncc';

            return (
              !!scenario.requestPhases.find(({ products }) => {
                return !!products.find(
                  p => p.productTypeID === productTypeID && p.isNCC === nccValue
                );
              }) &&
              !rates.find(
                r => r.productTypeID === productTypeID && r.isNCC === nccValue
              )
            );
          };

          const initialLoadState = groupBy(productTypeIDs, id => {
            return shouldLoad('ncc', id) || shouldLoad('ptsfees', id)
              ? productLoadState.NOT_STARTED
              : productLoadState.COMPLETE;
          });

          Object.keys(initialLoadState).forEach(state => {
            scenario.loadingState = updateLoadingState(
              state,
              scenario.loadingState,
              initialLoadState[state]
            );
          });
        }

        scenario.hasInitialData = !!getEnabledProducts().find(p => {
          return productTypeIDs.includes(p.productTypeID);
        });
      }

      return scenario;
    },
    [
      requestPhaseData,
      createRatesArray,
      createLoadingState,
      formatData,
      updateLoadingState,
      getEnabledProducts
    ]
  );

  return {
    createScenario,
    createRatesArray,
    productIsEnabled,
    getProductsToLoad,
    getRequestPhaseWithProductsToLoad,
    updateLoadingState,
    fetchRates,
    formatData
  };
}
